<template>
  <div class="page-wrapper">
    <!-- Modern Header -->
    <header class="header">
      <div class="header-container">
        <router-link to="/" class="logo-link">
          <img src="@/assets/logo.png" alt="Nexthome4U Logo" class="logo" />
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <div class="signup-card">
        <transition name="slide-fade" mode="out-in">
          <!-- Sign Up Form -->
          <div v-if="!isOtpMode" key="signup" class="form-container">
            <div class="card-header">
              <h1 class="title">Create Account</h1>
              <p class="subtitle">Join Nexthome4U and start managing your properties</p>
            </div>

            <form class="form" @submit="handleFormSubmit">
              <div class="form-group">
                <label for="email" class="label">Email Address</label>
                <div class="input-wrapper">
                  <input 
                    type="email" 
                    id="email" 
                    v-model="email" 
                    class="input"
                    placeholder="Enter your email"
                    required 
                    autocomplete="email"
                  />
                  <div class="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="mobile" class="label">Mobile Number</label>
                <div class="input-wrapper">
                  <span class="country-code">+977</span>
                  <input 
                    type="tel" 
                    id="mobile" 
                    v-model="mobile" 
                    class="input mobile-input"
                    placeholder="Enter 10-digit mobile number"
                    required 
                    autocomplete="tel"
                    inputmode="numeric"
                    maxlength="10"
                  />
                  <div class="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="password" class="label">Password</label>
                <div class="input-wrapper">
                  <input 
                    :type="showPassword ? 'text' : 'password'" 
                    id="password" 
                    v-model="password" 
                    class="input"
                    placeholder="Create a strong password"
                    required 
                    autocomplete="new-password"
                  />
                  <button 
                    type="button" 
                    class="password-toggle"
                    @click="showPassword = !showPassword"
                    tabindex="-1"
                  >
                    <svg v-if="!showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  </button>
                </div>
              </div>

              <button type="submit" class="submit-button" :disabled="isSubmitting">
                <span v-if="!isSubmitting">Create Account</span>
                <div v-if="isSubmitting" class="loading-spinner">
                  <div class="spinner"></div>
                  <span>Creating account...</span>
                </div>
              </button>

              <div class="divider">
                <span class="divider-text">Already have an account?</span>
              </div>

              <router-link to="/login" class="login-link">
                Sign in instead
              </router-link>
            </form>
          </div>

          <!-- OTP Verification -->
          <div v-else key="otp" class="otp-container">
            <div class="card-header">
              <h1 class="title">Verify Your Account</h1>
              <p class="subtitle">Enter the verification codes sent to your email and mobile number</p>
            </div>

            <div class="otp-sections">
              <div class="otp-section">
                <h3 class="otp-title">Email Verification</h3>
                <div class="otp-inputs">
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="emailOtp1" 
                    @input="focusNext($event, 'emailOtp2')"
                    @keydown="handleBackspace($event, 'emailOtp1', null)" 
                    ref="emailOtp1" 
                    class="otp-input" 
                    required
                  />
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="emailOtp2" 
                    @input="focusNext($event, 'emailOtp3')"
                    @keydown="handleBackspace($event, 'emailOtp2', 'emailOtp1')" 
                    ref="emailOtp2" 
                    class="otp-input" 
                    required
                  />
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="emailOtp3" 
                    @input="focusNext($event, 'emailOtp4')"
                    @keydown="handleBackspace($event, 'emailOtp3', 'emailOtp2')" 
                    ref="emailOtp3" 
                    class="otp-input" 
                    required
                  />
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="emailOtp4"
                    @keydown="handleBackspace($event, 'emailOtp4', 'emailOtp3')" 
                    ref="emailOtp4" 
                    class="otp-input" 
                    required
                  />
                </div>
              </div>

              <div class="otp-section">
                <h3 class="otp-title">Mobile Verification</h3>
                <div class="otp-inputs">
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="mobileOtp1" 
                    @input="focusNext($event, 'mobileOtp2')"
                    @keydown="handleBackspace($event, 'mobileOtp1', null)" 
                    ref="mobileOtp1" 
                    class="otp-input" 
                    required
                  />
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="mobileOtp2" 
                    @input="focusNext($event, 'mobileOtp3')"
                    @keydown="handleBackspace($event, 'mobileOtp2', 'mobileOtp1')" 
                    ref="mobileOtp2" 
                    class="otp-input" 
                    required
                  />
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="mobileOtp3" 
                    @input="focusNext($event, 'mobileOtp4')"
                    @keydown="handleBackspace($event, 'mobileOtp3', 'mobileOtp2')" 
                    ref="mobileOtp3" 
                    class="otp-input" 
                    required
                  />
                  <input 
                    type="text" 
                    maxlength="1" 
                    v-model="mobileOtp4"
                    @keydown="handleBackspace($event, 'mobileOtp4', 'mobileOtp3')" 
                    ref="mobileOtp4" 
                    class="otp-input" 
                    required
                  />
                </div>
              </div>
            </div>

            <button @click="verifyOtp" class="submit-button" :disabled="isVerifying">
              <span v-if="!isVerifying">Verify Account</span>
              <div v-if="isVerifying" class="loading-spinner">
                <div class="spinner"></div>
                <span>Verifying...</span>
              </div>
            </button>

            <div class="resend-section">
              <p class="resend-text">Didn't receive the codes?</p>
              <button type="button" class="resend-button" @click="resendCodes">
                Resend Codes
              </button>
            </div>
          </div>
        </transition>
      </div>
    </main>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper'; 

export default {
  name: 'SignupPage',
  data() {
    return {
      // Feature flag - set to true to enable signup functionality
      isSignupEnabled: true,
      email: '',
      mobile: '',
      password: '',
      showPassword: false,
      isSubmitting: false,
      isOtpMode: false,
      emailOtp1: '',
      emailOtp2: '',
      emailOtp3: '',
      emailOtp4: '',
      mobileOtp1: '',
      mobileOtp2: '',
      mobileOtp3: '',
      mobileOtp4: '',
      isVerifying: false,
      toasts: []
    };
  },
  methods: {
    getMobileWithCountryCode() {
      const mobileDigits = String(this.mobile || '').replace(/\D/g, '');
      if (!mobileDigits) return '';
      return mobileDigits.startsWith('977') ? `+${mobileDigits}` : `+977${mobileDigits}`;
    },
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now();
      this.toasts.push({ id, message, type });

      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },
    handleFormSubmit(event) {
      // First, let browser validation work
      if (!event.target.checkValidity()) {
        return; // Browser will show validation messages
      }
      
      // If validation passes, check our feature flag
      if (!this.isSignupEnabled) {
        event.preventDefault();
        this.showToast('Signup feature is coming soon! Stay tuned for updates.', 'info', 4000);
        return;
      }
      
      // If signup is enabled and validation passes, proceed
      event.preventDefault();
      this.submitForm();
    },
    async submitForm() {
      this.isSubmitting = true;
      try {
        const mobileWithCountryCode = this.getMobileWithCountryCode();
        const response = await apiService.signup({
          mobileno: mobileWithCountryCode,
          email: this.email,
          password: this.password,
          adminType: 'HOSTEL',
          productid: 'NEXTHOME4U'
        });
        console.log('Response Data:', response);
        this.isOtpMode = true;
      } catch (error) {
        console.error('Signup failed:', error);
        // Skip error handling for "no data" cases (status 400 with errorcode 2003)
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
          return;
        }
        
        let message = null;
        
        if (error?.response?.data) {
          const errorData = error.response.data;
          const status = error.response.status;
          
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            if (errorData.reason) {
              message = errorData.reason;
            }
            // If no reason provided for 400 error, don't show toast (no generic messages)
          } else {
            // For other status codes, prioritize reason field
            if (errorData.reason) {
              message = errorData.reason;
            }
          }
        } else if (error?.request) {
          message = 'No response from server. Please try again later.';
        } else if (error?.message) {
          message = error.message;
        }
        
        // Only show toast if we have a message (genuine errors only)
        if (message) {
          this.showToast(message, 'error');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    focusNext(event, nextField) {
      if (event.target.value.length === 1 && nextField) {
        this.$refs[nextField].focus();
      }
    },
    handleBackspace(event, currentField, prevField) {
      if (event.key === 'Backspace' && event.target.value === '' && prevField) {
        this[currentField] = '';
        this.$refs[prevField].focus();
      }
    },
    async verifyOtp() {
      const emailOtp = this.emailOtp1 + this.emailOtp2 + this.emailOtp3 + this.emailOtp4;
      const mobileOtp = this.mobileOtp1 + this.mobileOtp2 + this.mobileOtp3 + this.mobileOtp4;

      if (emailOtp.length !== 4 || mobileOtp.length !== 4) {
        this.showToast('Please enter the complete OTP for both email and mobile.', 'error');
        return;
      }

      const mobileWithCountryCode = this.getMobileWithCountryCode();
      this.isVerifying = true;
      apiService.verifyEmailMobileOtp(this.email, emailOtp, mobileWithCountryCode, mobileOtp)
      .then(response => {
          if (response.data.success) {
            this.showToast('Verification successful!', 'success', 3000);
            setTimeout(() => {
              this.$router.push('/login');
            }, 1000);
          } else {
            // For HTTP 400 errors, always use the reason field from API response
            if (response.data.reason) {
              this.showToast(response.data.reason, 'error');
            }
            // If no reason provided for 400 error, don't show toast (no generic messages)
          }
        })
        .catch(error => {
          console.error('OTP verification failed:', error);
          if (error.response) {
            // Skip error handling for "no data" cases (status 400 with errorcode 2003)
            if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
              return;
            }
            
            let message = null;
            if (error?.response?.data) {
              const errorData = error.response.data;
              const status = error.response.status;
              
              // For HTTP 400 errors, always use the reason field from API response
              if (status === 400 && errorData.reason) {
                message = errorData.reason;
              } else if (errorData.reason) {
                message = errorData.reason;
              }
            }
            
            if (message) {
              this.showToast(message, 'error');
            }
          } else if (error.request) {
            this.showToast('No response from server. Please try again.', 'error');
          } else {
            this.showToast('An error occurred during OTP verification.', 'error');
          }
        })
        .finally(() => {
          this.isVerifying = false;
        });
    },
    resendCodes() {
      // Reset OTP fields
      this.emailOtp1 = '';
      this.emailOtp2 = '';
      this.emailOtp3 = '';
      this.emailOtp4 = '';
      this.mobileOtp1 = '';
      this.mobileOtp2 = '';
      this.mobileOtp3 = '';
      this.mobileOtp4 = '';
      
      // Resend signup request
      this.submitForm();
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  background: #fcfbfb;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  position: sticky;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.logo-link {
  display: inline-block;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.05);
}

.logo {
  height: 40px;
  width: auto;
  max-width: 150px;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: calc(100vh - 80px);
}

.signup-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 
    0 32px 64px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 3rem;
  width: 100%;
  max-width: 480px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.subtitle {
  color: #6b7280;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

/* Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
  text-align: left;
}

.input-wrapper,
.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input,
.select {
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 400;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  outline: none;
}

.input:focus,
.select:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
  background: #ffffff;
}

.input:hover,
.select:hover {
  border-color: #d1d5db;
}

.input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.select {
  appearance: none;
  cursor: pointer;
}

.input-icon,
.select-icon {
  position: absolute;
  right: 1rem;
  color: #9ca3af;
  pointer-events: none;
}

.country-code {
  position: absolute;
  left: 1rem;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
  z-index: 1;
}

.mobile-input {
  padding-left: 4.2rem;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: #5b2e8e;
  background: rgba(91, 46, 142, 0.1);
}

.submit-button {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 48px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.4);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(91, 46, 142, 0.4);
}

.submit-button:active {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
}

.divider-text {
  background: rgba(255, 255, 255, 0.95);
  color: #6b7280;
  font-size: 14px;
  padding: 0 1rem;
  position: relative;
}

.login-link {
  display: block;
  text-align: center;
  color: #5b2e8e;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  padding: 0.75rem 1rem;
  border: 2px solid #5b2e8e;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.login-link:hover {
  background: #5b2e8e;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

/* OTP Verification */
.otp-container {
  width: 100%;
}

.otp-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;
}

.otp-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.otp-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  text-align: center;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.otp-input {
  width: 3.5rem;
  height: 3.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  outline: none;
}

.otp-input:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.otp-input:hover {
  border-color: #d1d5db;
}

.resend-section {
  text-align: center;
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.resend-text {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.resend-button {
  background: none;
  border: none;
  color: #5b2e8e;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.resend-button:hover {
  background: rgba(91, 46, 142, 0.1);
  text-decoration: underline;
}

/* Slide Fade Transition */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(30px) scale(0.95);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-30px) scale(0.95);
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 400px;
}

.toast {
  background: white;
  border-radius: 12px;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 300px;
}

.toast-error {
  border-left: 4px solid #ef4444;
}

.toast-success {
  border-left: 4px solid #10b981;
}

.toast-info {
  border-left: 4px solid #3b82f6;
}

.toast-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: -0.125rem;
}

.toast-close:hover {
  color: #6b7280;
  background: #f3f4f6;
}

/* Toast Animations */
.toast-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Responsive Design */
@media (max-width: 640px) {
  .main-content {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }

  .signup-card {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    margin-top: 1rem;
    max-width: none;
  }

  .title {
    font-size: 28px;
  }

  .otp-sections {
    gap: 1.5rem;
  }

  .otp-inputs {
    gap: 0.5rem;
  }

  .otp-input {
    width: 3rem;
    height: 3rem;
    font-size: 18px;
  }

  .toast-container {
    left: 1rem;
    right: 1rem;
    max-width: none;
  }

  .toast {
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .signup-card {
    padding: 1.5rem 1rem;
  }

  .title {
    font-size: 24px;
  }

  .subtitle {
    font-size: 14px;
  }

  .otp-inputs {
    gap: 0.375rem;
  }

  .otp-input {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 16px;
  }
}
</style>